import {apiErrorIdOf, CONTRACT_CANCELLATION_ALREADY_DECLARED_ERROR_ID} from "api/apiErrorIds";

/**
 * What a failed § 312k cancellation declaration tells the reader (kitecorp/transferify-ui#2327).
 * React-free, like every other rule in this feature.
 *
 * <p>Three answers only, and the shortness is the decision. A transport failure gets its own sentence because
 * the reader can act on it: check the connection, try again. A repeat declaration for the same address gets its
 * own sentence because it is not a failure at all (kitecorp/transferify-ui#2556): the first one was received, so
 * the reader must hear that, and where to write if its confirmation is missing, not "try again", which would make
 * them conclude their cancellation did not go through. It is read from the api's stable `errorId`, never from its
 * localized message or from the 429 status, which the abuse budgets answer too. Everything else is generic,
 * INCLUDING the abuse-budget 429: telling an anonymous caller that cancellations are rate limited invites them to
 * probe the limit, and the remedy the reader has is identical either way.
 *
 * <p>A 400 is also generic, and that is not laziness. The form validates the same rules the api does before
 * it posts, so a 400 here means the two have drifted — a defect for us to fix, not a sentence for the reader
 * to act on. It must never read as "you typed something wrong", because they did not.
 */

/** A key of the catalogue's `cancellation.errors` namespace. */
export type CancellationFailureKey = "network" | "alreadyDeclared" | "generic";

/** The axios-shaped fields this rule reads for the transport case. */
interface FailureLike {
	isAxiosError?: boolean;
	response?: {status?: number} | undefined;
}

export function cancellationFailureKey(failure: unknown): CancellationFailureKey {
	if (apiErrorIdOf(failure) === CONTRACT_CANCELLATION_ALREADY_DECLARED_ERROR_ID) {
		return "alreadyDeclared";
	}
	const candidate = failure as FailureLike | null | undefined;
	// An axios error with NO response never reached the server: DNS, offline, a refused connection, a
	// timeout. That is the one case where "check your connection" is true advice rather than a guess.
	return candidate?.isAxiosError === true && candidate.response === undefined ? "network" : "generic";
}
